import { Injectable } from '@angular/core';
import { Observable, catchError, firstValueFrom, map, throwError } from 'rxjs';

import { classToPlain, plainToClass } from 'class-transformer';
import { AngularFirestore, AngularFirestoreCollection, QueryFn } from '@angular/fire/compat/firestore';
import { UserModel } from 'src/app/models';
import { COLLECTION_NAMES } from './constants';
import { RolesService } from './roles.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private usersCollection: AngularFirestoreCollection<UserModel>;
  private usersCollectionQuery: (ref: QueryFn) => AngularFirestoreCollection<UserModel>;
  private usersWithPrivilege: any = {};
  private platformApiUpdateUserUrl: string;

  constructor(private afs: AngularFirestore, private rolesService: RolesService, private http: HttpClient) {
    this.usersCollection = afs.collection<UserModel>(COLLECTION_NAMES.USERS);
    this.usersCollectionQuery = (ref: QueryFn) => afs.collection<UserModel>(COLLECTION_NAMES.USERS, ref);
    this.platformApiUpdateUserUrl = environment.platformApi.url + '/update-user';
  }

  async addUser(user: UserModel): Promise<void> {
    const userId = user.id; // avoiding error prone scenarios
    return this.usersCollection
      .doc(userId)
      .ref.get()
      .then(result => {
        if (result.exists) {
          throw new Error(`User "${userId}" already exists`);
        } else {
          const plainUser = classToPlain(user);
          return this.usersCollection.doc(user.id).set(plainUser as UserModel);
        }
      });
  }

  async CanAccessAllCompanies(userId: string): Promise<boolean> {
    const user = await firstValueFrom(this.getUserById(userId));
    if (!user?.role) {
      return false;
    }

    const role = await firstValueFrom(this.rolesService.getRoleById(user.role));
    this.usersWithPrivilege[userId] = role?.permissions.find(x => x.toString() === 'ACCESS_ALL_COMPANIES') !== undefined;
    return this.usersWithPrivilege[userId];
  }

  isUserAbleAccessAllCompanies(userId: string): boolean {
    return this.usersWithPrivilege[userId] === true;
  }

  updateUser(user: UserModel, adminId?: string): Observable<void> {
    const data = {
      id: user.id,
      adminId,
      firstName: user.firstName,
      lastName: user.lastName,
      carLabsEmail: user.carLabsEmail,
      role: user.role,
      avatar: user.avatar,
      notificationSettings: user.notificationSettings,
    };

    return this.http.post<void>(this.platformApiUpdateUserUrl, data);
  }

  getUserById(id: string): Observable<UserModel | null> {
    return this.usersCollection
      .doc(id)
      .valueChanges()
      .pipe(
        map(user => {
          return plainToClass(UserModel, user);
        }),
        catchError(error => {
          console.error(`GetUserById exception: ${error}`);
          return throwError(error);
        }),
      );
  }

  getUsers(): Observable<UserModel[]> {
    return this.usersCollection.valueChanges().pipe(
      map(corps => corps.map(corp => plainToClass(UserModel, corp))),
      catchError(error => {
        console.error(`GetUsers exception: ${error}`);
        return throwError(error);
      }),
    );
  }

  removeUser(userId: string) {
    return this.usersCollection.doc(userId).delete();
  }

  getUsersByRole(role: string): Observable<UserModel[]> {
    const usersCollection = this.usersCollectionQuery(ref => ref.where('role', '==', role));
    return usersCollection.valueChanges().pipe(
      map(users => {
        return users.map(user => plainToClass(UserModel, user));
      }),
    );
  }
}
